import { useEffect, useMemo, useRef, useState } from 'react'

export const Breakpoints = {
  DesktopLarge: 'desktop-large',
  Desktop: 'desktop',
  Tablet: 'tablet',
  Mobile: 'mobile',
}

export type Breakpoint = (typeof Breakpoints)[keyof typeof Breakpoints]

export const BreakpointWidths = {
  DesktopLarge: 1920,
  Desktop: 1200,
  Mobile: 600,
}

export const MediaQuery = {
  minDesktop: `@media (min-width: 1201px)`,
  maxDesktop: `@media (max-width: 1920px)`,
  minTablet: `@media (min-width: 601px)`,
  maxTablet: `@media (max-width: 1200px)`,
  minMobile: `@media (min-width: 0px)`,
  maxMobile: `@media (max-width: 600px)`,
}

export const useBreakpoint = () => {
  const [clientWidth, setClientWidth] = useState<number>(() => window.innerWidth)

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const handler = (event: Event) => {
      if (!!event.currentTarget && event.type === 'resize') {
        const t = event.currentTarget as Window

        if (debounceRef.current) {
          clearTimeout(debounceRef.current)
        }

        debounceRef.current = setTimeout(() => {
          setClientWidth(t.outerWidth)
        }, 100)
      }
    }

    window.addEventListener('resize', handler)

    return () => {
      window.removeEventListener('resize', handler)
    }
  }, [])

  return useMemo(() => {
    return {
      clientWidth,
      minMobile: clientWidth > 0,
      maxMobile: clientWidth <= 600,
      minTablet: clientWidth > 601,
      maxTablet: clientWidth <= 1200,
      minDesktop: clientWidth > 1200,
      maxDesktop: clientWidth <= 1920,
      minLarge: clientWidth > 1920,
    }
  }, [clientWidth])
}
